"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

interface ProductsHeaderProps {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  totalCount: number;
}

export default function ProductsHeader({ searchQuery, onSearchChange, totalCount }: ProductsHeaderProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = ref.current?.querySelectorAll(".hero-reveal");
    if (!els) return;
    els.forEach((el, i) => {
      (el as HTMLElement).style.animationDelay = `${i * 0.12}s`;
      el.classList.add("animate-fade-up");
    });
  }, []);

  return (
    <section
      ref={ref}
      className="relative pt-28 pb-16 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #EDE7FF 0%, #FFF0FA 40%, #D6EEFF 100%)",
      }}
      aria-label="Products page header"
    >
      {/* Decorative circles */}
      <div className="absolute top-10 right-10 w-40 h-40 rounded-full blur-3xl opacity-30" style={{ background: "var(--peach)" }} />
      <div className="absolute bottom-0 left-10 w-32 h-32 rounded-full blur-2xl opacity-25" style={{ background: "var(--mint)" }} />
      <div className="absolute top-20 left-1/3 text-3xl star-pulse" aria-hidden="true">⭐</div>
      <div className="absolute top-16 right-1/3 text-2xl star-pulse" style={{ animationDelay: "1s" }} aria-hidden="true">✨</div>

      <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
        <div className="hero-reveal opacity-0 inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-lavender/40 rounded-full px-4 py-2 text-xs font-bold text-plum mb-5 shadow-dreamy">
          <Icon name="SparklesIcon" size={14} variant="solid" className="text-gold" />
          {totalCount} Magical Products Available
        </div>

        <h1 className="hero-reveal opacity-0 font-fraunces text-5xl md:text-6xl font-black text-plum leading-tight mb-4">
          The Full
          <span className="italic text-plum-light"> Collection</span>
        </h1>
        <p className="hero-reveal opacity-0 text-plum/65 text-lg max-w-xl mx-auto mb-8">
          Every book, workbook, and accessory your little one needs to explore, create, and learn.
        </p>

        {/* Search bar */}
        <div className="hero-reveal opacity-0 max-w-md mx-auto">
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-plum/40" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search books, alphabets, numbers..."
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white/90 backdrop-blur border border-lavender/40 text-plum placeholder:text-plum/40 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-lavender shadow-dreamy"
              aria-label="Search products"
            />
          </div>
        </div>
      </div>

      {/* Wave */}
      <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1440 40" fill="none">
          <path d="M0 40L720 10L1440 40V40H0Z" fill="#FFF8F0" />
        </svg>
      </div>
    </section>
  );
}